import * as React from "react";
import { useTranslation } from "react-i18next";

import { Box, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LineChart } from "@mui/x-charts/LineChart";
import { Dayjs } from "dayjs";
import axios from "axios";

import useSnackbarAlert from "../../../utils/snackbarHandlers";
// Card icons (provided assets, Figma node 2020:1293)
import salesIcon from "../../common/Images/sales.png";
import lastMonthIcon from "../../common/Images/lastMonth.png";
import searchIcon from "../../common/Images/search.png";

// Sales Insights v2 palette (exact Figma spec, node 2020:1293)
const C = {
  // exact card colors (Figma spec: header bg + inset glow use the same hex)
  green: "#007D21",
  orange: "#F65314",
  purple: "#8D1D57",
  greenShadow: "#007D21",
  orangeShadow: "#F65314",
  purpleShadow: "#8D1D57",
  // chart series
  chartSales: "#2fb344",
  chartConfirms: "#2f6df6",
  chartActivations: "#f47216",
  // chart tabs / area (Figma nodes 2020:1563)
  tabBlueActive: "#0080FF",
  tabBlueGlow: "#0080FF",
  // games tabs / table (Figma nodes 2020:1596)
  tabGreenActive: "#01C134",
  tabGreenGlow: "#00DE3B",
};

interface Summary {
  thisMonthSale: number;
  lastMonthSale: number;
  projectedSale: number;
  confirmed: { tickets: number; amount: number };
  activated: { tickets: number; amount: number };
  inventory: { tickets: number; amount: number };
}

interface Point { date: string; sales: number; confirms: number; activations: number; }
interface TopGame { gameNumber: string; name: string; imageUrl: string; gameAmount: string; totalSales: number; ticketsSold: number; }

type ChartPeriod = "thisMonth" | "lastMonth" | "monthly";
type GamesPeriod = "week" | "month" | "lastMonth" | "allTime" | "custom";

const SalesInsightsV2: React.FC = () => {
  //@ts-ignore
  const config = window.config;
  const apiBaseURL = config?.server || "http://18.217.95.31:8080";
  const currencySymbol = config?.currencySymbol ?? "$";
  const shopId = JSON.parse(localStorage.getItem("UserData") || "{}")?.uId;
  const { t } = useTranslation("translation");
  const { showErrorMessage } = useSnackbarAlert();

  const money = (v: number) => `${currencySymbol}${Number.isInteger(v) ? v : (v || 0).toFixed(2)}`;

  const [summary, setSummary] = React.useState<Summary | null>(null);
  const [chartPeriod, setChartPeriod] = React.useState<ChartPeriod>("thisMonth");
  const [points, setPoints] = React.useState<Point[]>([]);
  const [gamesPeriod, setGamesPeriod] = React.useState<GamesPeriod>("week");
  const [games, setGames] = React.useState<TopGame[]>([]);
  const [fromDate, setFromDate] = React.useState<Dayjs | null>(null);
  const [toDate, setToDate] = React.useState<Dayjs | null>(null);
  const [hiddenSeries, setHiddenSeries] = React.useState<Record<string, boolean>>({});

  // ---- summary cards ----
  React.useEffect(() => {
    axios
      .post(`${apiBaseURL}/api/inventory-v2/insights/summary`, { userId: shopId })
      .then((res: any) => setSummary(res?.data ?? null))
      .catch(() => showErrorMessage(t("somethingWentWrong", "Something went wrong")));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- chart series ----
  React.useEffect(() => {
    axios
      .post(`${apiBaseURL}/api/inventory-v2/insights/timeseries`, { userId: shopId, period: chartPeriod })
      .then((res: any) => setPoints(res?.data?.points ?? []))
      .catch(() => showErrorMessage(t("somethingWentWrong", "Something went wrong")));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartPeriod]);

  // ---- top games ----
  const loadGames = React.useCallback(() => {
    const body: any = { userId: shopId, period: gamesPeriod };
    if (gamesPeriod === "custom") {
      if (!fromDate || !toDate) return; // wait for both dates
      if (toDate.isBefore(fromDate)) {
        return showErrorMessage(t("dateRangeInvalid", "The end date cannot be before the start date"));
      }
      body.fromDate = fromDate.format("YYYY-MM-DD");
      body.toDate = toDate.format("YYYY-MM-DD");
    }
    axios
      .post(`${apiBaseURL}/api/inventory-v2/insights/topGames`, body)
      .then((res: any) => setGames(res?.data?.games ?? []))
      .catch(() => showErrorMessage(t("somethingWentWrong", "Something went wrong")));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gamesPeriod, fromDate, toDate]);

  React.useEffect(() => { loadGames(); }, [loadGames]);

  // chart series definitions; clicking a legend item toggles that series on/off
  const seriesDefs = [
    { key: "sales", color: C.chartSales, label: t("salesSeries", "Sales"), get: (p: Point) => p.sales },
    { key: "confirms", color: C.chartConfirms, label: t("confirmsSeries", "Confirms"), get: (p: Point) => p.confirms },
    { key: "activations", color: C.chartActivations, label: t("activationsSeries", "Activations"), get: (p: Point) => p.activations },
  ];
  const toggleSeries = (key: string) => setHiddenSeries((prev) => ({ ...prev, [key]: !prev[key] }));

  // ---- metric card (icon image + colored pill + sub-card, with the Figma inset glow) ----
  const MetricCard = (props: {
    iconSrc: string; title: string; value: string;
    subTitle: string; subTickets: number; subAmount: string; color: string; shadowColor: string;
    iconRingColor?: string; iconSize?: number; iconNudge?: string;
  }) => (
    <Box sx={{ flex: "1 1 340px", minWidth: "340px" }}>
      {/* top: a strongly-rounded colored card with the icon overlapping its left
          border. Header on the color, then a white rounded value inset (value in
          the card color) floating in the card. */}
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {/* white circular background so the transparent icon never shows the card color behind it */}
        <Box sx={{ width: 92, height: 92, borderRadius: "50%", backgroundColor: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, position: "relative", zIndex: 2, mr: "-44px", boxShadow: "0 2px 8px rgba(0,0,0,0.12)" }}>
          {props.iconRingColor ? (
            // inner colored ring placed like the green ring on the This Month card
            // (~82px, leaving a small white margin outside the ring)
            <Box sx={{ width: 77, height: 75, borderRadius: "50%", border: `4px solid ${props.iconRingColor}`, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Box component='img' src={props.iconSrc} alt='' sx={{ width: props.iconSize ?? 58, height: props.iconSize ?? 58, objectFit: "contain", transform: props.iconNudge ?? "none" }} />
            </Box>
          ) : (
            <Box component='img' src={props.iconSrc} alt='' sx={{ width: props.iconSize ?? 82, height: props.iconSize ?? 82, objectFit: "contain", transform: props.iconNudge ?? "none" }} />
          )}
        </Box>
        {/* same header-band + full-width white body treatment as the sub-card, with the
            icon overlapping the pill's left edge (no floating inner box, no side frame) */}
        <Box sx={{ flex: 1, borderRadius: "30px", overflow: "hidden" }}>
          <Box sx={{ backgroundColor: props.color, py: "6px", pl: "46px" }}>
            <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: "25px", lineHeight: 1.2, textAlign: "center", letterSpacing: "0.5px" }}>{props.title}</Typography>
          </Box>
          <Box sx={{ backgroundColor: "#fff", py: "14px", pl: "46px", textAlign: "center", boxShadow: `inset 0px 4px 20px 0px ${props.color}` }}>
            <Typography sx={{ fontSize: "40px", fontWeight: 800, lineHeight: 1.1, color: props.color }}>{props.value}</Typography>
          </Box>
        </Box>
      </Box>
      {/* sub-card (exact Figma spec, DigitalDisplays.png): a full-width green header
          band on top and a full-width white body directly beneath it with the inset
          glow. Rounded TOP corners only - the bottom is a flat/square white edge with
          no border color. Labels in the card color; numbers black. */}
      <Box sx={{ mt: "12px", ml: "46px", borderRadius: "14px 14px 0 0", overflow: "hidden" }}>
        <Box sx={{ backgroundColor: props.color, py: "6px" }}>
          <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: "25px", lineHeight: 1.2, textAlign: "center" }}>{props.subTitle}</Typography>
        </Box>
        <Box sx={{ backgroundColor: "#fff", py: "22px", textAlign: "center", boxShadow: `inset 0px 4px 20px 0px ${props.color}` }}>
          <Typography sx={{ fontSize: "20px", fontWeight: 700, color: props.color, mb: "2px" }}>{t("totalTicketLabel", "Total Ticket")}</Typography>
          <Typography sx={{ fontSize: "22px", fontWeight: 800, color: "#000", mb: "14px" }}>{props.subTickets}</Typography>
          <Typography sx={{ fontSize: "20px", fontWeight: 700, color: props.color, mb: "2px" }}>{t("amountLabel", "Amount")}</Typography>
          <Typography sx={{ fontSize: "22px", fontWeight: 800, color: "#000" }}>{props.subAmount}</Typography>
        </Box>
      </Box>
    </Box>
  );

  // ---- period tabs: folder-style tabs attached to the card below. Active = solid
  // color; inactive = white with an inset colored glow (Figma spec 2020:1563 / 2020:1596).
  const Tabs = <T extends string>(props: { options: { label: string; value: T }[]; value: T; onChange: (v: T) => void; activeBg: string; glowColor: string }) => (
    <Box display='flex' flexWrap='wrap' gap='6px' sx={{ position: "relative", zIndex: 2, ml: "6px", mb: "-1px" }}>
      {props.options.map((o) => {
        const active = o.value === props.value;
        return (
          <Box
            key={o.value}
            onClick={() => props.onChange(o.value)}
            sx={{
              px: "22px",
              py: "10px",
              cursor: "pointer",
              fontSize: "15px",
              fontWeight: active ? 800 : 700,
              color: active ? "#fff" : "#111",
              background: active ? props.activeBg : "#fff",
              boxShadow: active ? "none" : `inset 0px 4px 20px 0px ${props.glowColor}`,
              border: `1px solid ${props.activeBg}`,
              borderBottom: active ? `1px solid ${props.activeBg}` : "none",
              borderRadius: "10px 10px 0 0",
            }}
          >
            {o.label}
          </Box>
        );
      })}
    </Box>
  );

  const thSx = { fontSize: "15px", fontWeight: 800, color: "#111", textAlign: "center", py: "12px", px: "8px", borderBottom: `2px solid ${C.tabGreenActive}55` } as const;
  const tdSx = { fontSize: "15px", color: "#222", textAlign: "center", py: "8px", px: "8px", borderBottom: "1px solid #e4efe8" } as const;

  return (
    <Box p={2}>
      <Typography sx={{ fontSize: "30px", fontWeight: 700, color: "#000", borderBottom: "1px solid #e0e0e0", pb: 1, mb: 2 }}>
        {t("salesInsights", "Sales Insights")}
      </Typography>

      {/* Metric cards */}
      <Box display='flex' gap='20px' flexWrap='wrap' mb={3}>
        <MetricCard
          iconSrc={salesIcon}
          title={t("thisMonthSaleCard", "THIS MONTH SALE")}
          value={money(summary?.thisMonthSale ?? 0)}
          subTitle={t("confirmedCardTitle", "CONFIRMED")}
          subTickets={summary?.confirmed?.tickets ?? 0}
          subAmount={money(summary?.confirmed?.amount ?? 0)}
          color={C.green}
          shadowColor={C.greenShadow}
        />
        <MetricCard
          iconSrc={lastMonthIcon}
          title={t("lastMonthSaleCard", "LAST MONTH SALE")}
          value={money(summary?.lastMonthSale ?? 0)}
          subTitle={t("activatedCardTitle", "ACTIVATED")}
          subTickets={summary?.activated?.tickets ?? 0}
          subAmount={money(summary?.activated?.amount ?? 0)}
          color={C.orange}
          shadowColor={C.orangeShadow}
        />
        <MetricCard
          iconSrc={searchIcon}
          title={t("projectedSaleCard", "PROJECTED SALE")}
          value={money(summary?.projectedSale ?? 0)}
          subTitle={t("inventoryCardTitle", "INVENTORY")}
          subTickets={summary?.inventory?.tickets ?? 0}
          subAmount={money(summary?.inventory?.amount ?? 0)}
          color={C.purple}
          shadowColor={C.purpleShadow}
          iconRingColor={C.purple}
          iconSize={40}
          iconNudge='translate(2px, 2px)'
        />
      </Box>

      {/* Chart */}
      <Tabs
        options={[
          { label: t("thisMonthTab", "This Month"), value: "thisMonth" },
          { label: t("lastMonthTab", "Last Month"), value: "lastMonth" },
          { label: t("monthlyTab", "Monthly"), value: "monthly" },
        ]}
        value={chartPeriod}
        onChange={setChartPeriod}
        activeBg={C.tabBlueActive}
        glowColor={C.tabBlueGlow}
      />
      <Box sx={{ border: `1px solid ${C.tabBlueActive}`, borderRadius: "0px 12px 12px 12px", p: 1.5, mb: 3, boxShadow: `inset 0px 1px 20px 0px ${C.tabBlueGlow}`, backgroundColor: "#fff" }}>
        {/* custom legend kept above the plot so it never overlaps the axis/units */}
        <Box display='flex' justifyContent='center' gap='22px' flexWrap='wrap' mb={0.5}>
          {seriesDefs.map((s) => {
            const off = !!hiddenSeries[s.key];
            return (
              <Box key={s.key} onClick={() => toggleSeries(s.key)} sx={{ display: "flex", alignItems: "center", gap: "7px", cursor: "pointer", userSelect: "none", opacity: off ? 0.4 : 1 }}>
                <Box sx={{ width: 24, height: 5, borderRadius: "3px", backgroundColor: s.color }} />
                <Typography sx={{ fontSize: "15px", color: "#333", textDecoration: off ? "line-through" : "none" }}>{s.label}</Typography>
              </Box>
            );
          })}
        </Box>
        <LineChart
          height={360}
          xAxis={[{ scaleType: "point", data: points.map((p) => p.date), tickLabelStyle: { angle: -45, textAnchor: "end", fontSize: 11 } }]}
          yAxis={[{ min: 0, valueFormatter: (v: number) => `${Math.round(v)}` }]}
          series={seriesDefs.filter((s) => !hiddenSeries[s.key]).map((s) => ({ data: points.map(s.get), label: s.label, color: s.color, showMark: true }))}
          margin={{ left: 64, right: 24, top: 10, bottom: 84 }}
          slotProps={{ legend: { hidden: true } }}
        />
      </Box>

      {/* Top 10 selling games */}
      <Box display='flex' alignItems='flex-end' gap='12px' flexWrap='wrap'>
        <Tabs
          options={[
            { label: t("thisWeekTab", "This Week"), value: "week" },
            { label: t("thisMonthTab", "This Month"), value: "month" },
            { label: t("lastMonthTab", "Last Month"), value: "lastMonth" },
            { label: t("allTimeTab", "All Time"), value: "allTime" },
            { label: t("customTab", "Custom"), value: "custom" },
          ]}
          value={gamesPeriod}
          onChange={setGamesPeriod}
          activeBg={C.tabGreenActive}
          glowColor={C.tabGreenGlow}
        />
        {gamesPeriod === "custom" && (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box display='flex' alignItems='center' gap='10px' sx={{ mb: "6px" }}>
              <DatePicker value={fromDate} onChange={setFromDate} format='MM/DD/YYYY' slotProps={{ textField: { size: "small", sx: { backgroundColor: "#fff", width: "160px" } } }} />
              <Typography sx={{ fontSize: "15px" }}>{t("dateRangeTo", "to")}</Typography>
              <DatePicker value={toDate} onChange={setToDate} format='MM/DD/YYYY' minDate={fromDate ?? undefined} slotProps={{ textField: { size: "small", sx: { backgroundColor: "#fff", width: "160px" } } }} />
            </Box>
          </LocalizationProvider>
        )}
      </Box>

      <Box sx={{ border: `1px solid ${C.tabGreenActive}`, borderRadius: "0px 12px 12px 12px", overflow: "hidden", boxShadow: `inset 0px 1px 20px 0px ${C.tabGreenActive}`, backgroundColor: "#fff" }}>
        <Box sx={{ width: "100%", overflowX: "auto" }}>
          <Box component='table' sx={{ width: "100%", borderCollapse: "collapse", minWidth: "760px" }}>
            <Box component='thead'>
              <Box component='tr' sx={{ backgroundColor: "#fff" }}>
                {[t("imageHeaderText", "Image"), t("gameNumberHeaderText2", "Game Number"), t("nameHeaderText", "Name"), t("gameAmountHeaderText", "Game Amount"), t("totalSalesHeaderText", "Total Sales")].map((h) => (
                  <Box component='th' key={h} sx={thSx}>{h}</Box>
                ))}
              </Box>
            </Box>
            <Box component='tbody'>
              {games.map((g) => (
                <Box component='tr' key={g.gameNumber}>
                  <Box component='td' sx={tdSx}>
                    {g.imageUrl ? <Box component='img' src={g.imageUrl} alt={g.name} sx={{ height: "40px", width: "auto" }} /> : "-"}
                  </Box>
                  <Box component='td' sx={tdSx}>{g.gameNumber}</Box>
                  <Box component='td' sx={tdSx}>{g.name}</Box>
                  <Box component='td' sx={tdSx}>{`${currencySymbol}${g.gameAmount}`}</Box>
                  <Box component='td' sx={tdSx}>{money(g.totalSales)}</Box>
                </Box>
              ))}
              {!games.length && (
                <Box component='tr'>
                  <Box component='td' colSpan={5} sx={{ ...tdSx, py: 3, color: "#777" }}>
                    {t("noTopGames", "No sales in this period")}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
        <Typography sx={{ backgroundColor: C.tabGreenActive, color: "#fff", textAlign: "center", fontWeight: 700, fontSize: "15px", py: "8px" }}>
          {t("topSellingGames", "Top 10 Selling Games")}
        </Typography>
      </Box>
    </Box>
  );
};

export default SalesInsightsV2;
